import React, { useRef, useEffect } from 'react';
import { useWebRTC } from '../hooks/useWebRTC';

interface RehearsalVideoChatProps {
  rehearsalId: string;
  userId: string;
  userName?: string;
  autoConnect?: boolean;
}

/**
 * RehearsalVideoChat component provides audio/video communication for rehearsal sessions
 * Implements Requirement 27.3 (peer-to-peer streams for rehearsal participants)
 */
export const RehearsalVideoChat: React.FC<RehearsalVideoChatProps> = ({
  rehearsalId,
  userId,
  userName,
  autoConnect = false
}) => {
  const {
    localStream,
    participants,
    isConnected,
    error,
    connect,
    disconnect,
    toggleAudio,
    toggleVideo,
    isAudioEnabled,
    isVideoEnabled
  } = useWebRTC({ rehearsalId, userId, userName, autoConnect });

  const localVideoRef = useRef<HTMLVideoElement>(null);

  // Set local video stream
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  return (
    <div className="rehearsal-video-chat">
      <div className="video-chat-header">
        <h3>Rehearsal Video Chat</h3>
        <div className="connection-status">
          {isConnected ? (
            <span className="status-connected">Connected</span>
          ) : (
            <span className="status-disconnected">Disconnected</span>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <div className="video-grid">
        {/* Local video */}
        <div className="video-container local-video">
          <video
            ref={localVideoRef}
            autoPlay
            muted
            playsInline
            className="video-element"
          />
          <div className="video-label">You {userName && `(${userName})`}</div>
        </div>

        {/* Remote videos */}
        {Array.from(participants.entries()).map(([participantId, participant]) => (
          <RemoteVideo
            key={participantId}
            participant={participant}
          />
        ))}
      </div>

      <div className="video-controls">
        {!isConnected ? (
          <button onClick={connect} className="btn-connect">
            Join Rehearsal
          </button>
        ) : (
          <>
            <button
              onClick={toggleAudio}
              className={`btn-control ${isAudioEnabled ? 'active' : 'inactive'}`}
              aria-label={isAudioEnabled ? 'Mute audio' : 'Unmute audio'}
            >
              {isAudioEnabled ? '🎤 Mute' : '🔇 Unmute'}
            </button>
            <button
              onClick={toggleVideo}
              className={`btn-control ${isVideoEnabled ? 'active' : 'inactive'}`}
              aria-label={isVideoEnabled ? 'Turn off video' : 'Turn on video'}
            >
              {isVideoEnabled ? '📹 Video Off' : '📹 Video On'}
            </button>
            <button onClick={disconnect} className="btn-disconnect">
              Leave Rehearsal
            </button>
          </>
        )}
      </div>

      <style>{`
        .rehearsal-video-chat {
          padding: 1rem;
          background: #f5f5f5;
          border-radius: 8px;
        }

        .video-chat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .video-chat-header h3 {
          margin: 0;
          font-size: 1.25rem;
        }

        .connection-status {
          font-size: 0.875rem;
        }

        .status-connected {
          color: #22c55e;
          font-weight: 600;
        }

        .status-disconnected {
          color: #ef4444;
          font-weight: 600;
        }

        .error-message {
          padding: 0.75rem;
          background: #fee2e2;
          color: #991b1b;
          border-radius: 4px;
          margin-bottom: 1rem;
        }

        .video-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .video-container {
          position: relative;
          background: #000;
          border-radius: 8px;
          overflow: hidden;
          aspect-ratio: 16 / 9;
        }

        .video-element {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .video-label {
          position: absolute;
          bottom: 0.5rem;
          left: 0.5rem;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.875rem;
        }

        .local-video {
          border: 2px solid #3b82f6;
        }

        .video-controls {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
        }

        .btn-connect,
        .btn-disconnect,
        .btn-control {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 4px;
          font-size: 1rem;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-connect {
          background: #22c55e;
          color: white;
        }

        .btn-connect:hover {
          background: #16a34a;
        }

        .btn-disconnect {
          background: #ef4444;
          color: white;
        }

        .btn-disconnect:hover {
          background: #dc2626;
        }

        .btn-control {
          background: #3b82f6;
          color: white;
        }

        .btn-control:hover {
          background: #2563eb;
        }

        .btn-control.inactive {
          background: #6b7280;
        }

        .btn-control.inactive:hover {
          background: #4b5563;
        }
      `}</style>
    </div>
  );
};

interface RemoteVideoProps {
  participant: {
    userId: string;
    userName?: string;
    stream?: MediaStream;
  };
}

const RemoteVideo: React.FC<RemoteVideoProps> = ({ participant }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && participant.stream) {
      videoRef.current.srcObject = participant.stream;
    }
  }, [participant.stream]);

  return (
    <div className="video-container remote-video">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="video-element"
      />
      <div className="video-label">
        {participant.userName || participant.userId}
      </div>
    </div>
  );
};
